import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { fourthDimensionAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { 
    ArrowLeft, Users, Clock, DollarSign, Sparkles, Lightbulb, 
    Music, Monitor, Coffee, Wand2, ChevronLeft, ChevronRight,
    Calendar as CalendarIcon, Check, X, Shield, Table, Play,
    Image, Settings
} from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore } from 'date-fns';

const FEATURE_ICONS = {
    table: Table,
    lightbulb: Lightbulb,
    wand: Wand2,
    music: Music,
    monitor: Monitor,
    coffee: Coffee,
};

const FourthDimension = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    // State
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [dateAvailability, setDateAvailability] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const [daySlots, setDaySlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    
    // Booking form
    const [bookingForm, setBookingForm] = useState({
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        party_size: 4,
        game_type: 'general',
        special_requests: '',
    });

    // Fetch room info
    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const response = await fourthDimensionAPI.getInfo();
                setInfo(response.data);
            } catch (error) {
                console.error('Error fetching fourth-dimension info:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchInfo();
    }, []);

    // Check admin status
    useEffect(() => {
        const checkAdmin = async () => {
            if (user) {
                try {
                    const response = await fourthDimensionAPI.checkAdmin();
                    setIsAdmin(response.data.is_admin);
                } catch (error) {
                    setIsAdmin(false);
                }
            }
        };
        checkAdmin();
    }, [user]);

    // Fetch month availability
    useEffect(() => {
        const fetchMonthAvailability = async () => {
            try {
                const month = currentMonth.getMonth() + 1;
                const year = currentMonth.getFullYear();
                const response = await fourthDimensionAPI.getDateAvailability(month, year);
                setDateAvailability(response.data);
            } catch (error) {
                console.error('Error fetching availability:', error);
            }
        };
        fetchMonthAvailability();
    }, [currentMonth]);

    // Fetch day slots when date selected
    useEffect(() => {
        if (selectedDate) {
            const fetchDaySlots = async () => {
                setLoadingSlots(true);
                try {
                    const dateStr = format(selectedDate, 'yyyy-MM-dd');
                    const response = await fourthDimensionAPI.getAvailability({
                        start_date: dateStr,
                        end_date: dateStr
                    });
                    setDaySlots(response.data);
                } catch (error) {
                    console.error('Error fetching slots:', error);
                    setDaySlots([]);
                } finally {
                    setLoadingSlots(false);
                }
            };
            fetchDaySlots();
        }
    }, [selectedDate]);

    // Handle booking
    const handleBooking = async () => {
        if (!bookingForm.guest_name || !bookingForm.guest_email) {
            toast.error('Please fill in your name and email');
            return;
        }

        try {
            await fourthDimensionAPI.createBooking({
                slot_id: selectedSlot.slot_id,
                ...bookingForm
            });
            toast.success('Booking request submitted! We\'ll confirm shortly.');
            setShowBookingModal(false);
            setSelectedSlot(null);
            setBookingForm({
                guest_name: '',
                guest_email: '',
                guest_phone: '',
                party_size: 4,
                game_type: 'general',
                special_requests: '',
            });
            // Refresh slots
            if (selectedDate) {
                const dateStr = format(selectedDate, 'yyyy-MM-dd');
                const response = await fourthDimensionAPI.getAvailability({
                    start_date: dateStr,
                    end_date: dateStr
                });
                setDaySlots(response.data);
            }
        } catch (error) {
            console.error('Booking error:', error);
            toast.error(error.response?.data?.detail || 'Failed to create booking');
        }
    };

    // Generate calendar days
    const calendarDays = eachDayOfInterval({
        start: startOfMonth(currentMonth),
        end: endOfMonth(currentMonth)
    });

    // Get day status
    const getDayStatus = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const avail = dateAvailability[dateStr];
        
        if (!avail) return 'unavailable';
        if (avail.blocked) return 'blocked';
        if (avail.available > 0) return 'available';
        if (avail.booked > 0) return 'booked';
        return 'unavailable';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="matrix-loader" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="fourth-dimension-page">
            <div className="scanlines" />
            
            {/* Hero Section */}
            <header className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#001a00] via-[#050505] to-[#050505]">
                    <div className="absolute inset-0 opacity-20">
                        {[...Array(50)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute w-1 h-1 bg-[#00ff41] rounded-full"
                                style={{
                                    left: `${Math.random() * 100}%`,
                                    top: `${Math.random() * 100}%`,
                                }}
                                animate={{
                                    opacity: [0.2, 1, 0.2],
                                    scale: [1, 1.5, 1],
                                }}
                                transition={{
                                    duration: 2 + Math.random() * 2,
                                    repeat: Infinity,
                                    delay: Math.random() * 2,
                                }}
                            />
                        ))}
                    </div>
                </div>

                <div className="relative z-10 text-center px-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/dashboard')}
                        className="absolute top-4 left-4 text-[#00ff41]"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>

                    {isAdmin && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAdminPanel(true)}
                            className="absolute top-4 right-4 border-[#003b00] text-[#00ff41]"
                        >
                            <Settings className="w-4 h-4 mr-2" />
                            Admin
                        </Button>
                    )}

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="inline-block mb-6">
                            <div className="terminal-window px-6 py-2">
                                <span className="text-[#00ff41] font-mono text-sm">4th_dimension.exe</span>
                            </div>
                        </div>

                        <h1 className="text-5xl md:text-7xl font-bold text-[#00ff41] mb-4 tracking-wider glow-text">
                            4TH DIMENSION
                        </h1>
                        <p className="text-xl md:text-2xl text-[#008f11] font-mono mb-8">
                            {info?.tagline}
                        </p>
                        <p className="max-w-2xl mx-auto text-[#008f11] mb-8">
                            {info?.description}
                        </p>

                        <div className="flex justify-center gap-4 flex-wrap">
                            <Button
                                data-testid="book-now-btn"
                                onClick={() => document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' })}
                                className="bg-[#00ff41] text-black hover:bg-[#00ff41]/80 px-8 py-6 text-lg"
                            >
                                <CalendarIcon className="w-5 h-5 mr-2" />
                                BOOK_NOW
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' })}
                                className="border-[#003b00] text-[#00ff41] hover:bg-[#00ff41]/10 px-8 py-6 text-lg"
                            >
                                <Play className="w-5 h-5 mr-2" />
                                EXPLORE
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </header>

            {/* Features Section */}
            <section id="features-section" className="py-20 px-4">
                <div className="container mx-auto max-w-6xl">
                    <motion.h2 
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        className="text-3xl font-bold text-[#00ff41] text-center mb-4"
                    >
                        IMMERSIVE_FEATURES
                    </motion.h2>
                    <p className="text-[#008f11] text-center mb-12 max-w-2xl mx-auto">
                        Every detail designed to transport you into your game world
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {info?.features?.map((feature, index) => {
                            const IconComponent = FEATURE_ICONS[feature.icon] || Sparkles;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="terminal-window p-6 hover:border-[#00ff41] transition-colors"
                                >
                                    <div className="w-12 h-12 bg-[#003b00] rounded-lg flex items-center justify-center mb-4">
                                        <IconComponent className="w-6 h-6 text-[#00ff41]" />
                                    </div>
                                    <h3 className="text-[#00ff41] font-bold mb-2">{feature.title}</h3>
                                    <p className="text-[#008f11] text-sm">{feature.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Gallery Placeholder */}
            <section className="py-20 px-4 bg-[#0a0a0a]">
                <div className="container mx-auto max-w-6xl">
                    <h2 className="text-3xl font-bold text-[#00ff41] text-center mb-12">
                        GALLERY
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                className="aspect-video bg-[#050505] border border-[#003b00] flex items-center justify-center"
                            >
                                <div className="text-center">
                                    <Image className="w-8 h-8 text-[#003b00] mx-auto mb-2" />
                                    <p className="text-[#003b00] text-xs font-mono">Image {i}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                    <p className="text-[#008f11] text-center mt-4 text-sm">
                        Gallery images coming soon
                    </p>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-20 px-4">
                <div className="container mx-auto max-w-4xl">
                    <h2 className="text-3xl font-bold text-[#00ff41] text-center mb-4">
                        PRICING
                    </h2>
                    <p className="text-[#008f11] text-center mb-12">
                        Choose your adventure length
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {info?.pricing?.map((tier, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className={`terminal-window p-6 text-center ${index === 1 ? 'border-[#00ff41] scale-105' : ''}`}
                            >
                                {index === 1 && (
                                    <div className="bg-[#00ff41] text-black text-xs font-bold px-3 py-1 inline-block mb-4">
                                        POPULAR
                                    </div>
                                )}
                                <h3 className="text-[#00ff41] font-bold text-xl mb-2">{tier.name}</h3>
                                <div className="flex items-center justify-center gap-1 mb-2">
                                    <Clock className="w-4 h-4 text-[#008f11]" />
                                    <span className="text-[#008f11]">{tier.duration}</span>
                                </div>
                                <div className="text-4xl font-bold text-[#00ff41] mb-4">
                                    ${tier.price}
                                </div>
                                <p className="text-[#008f11] text-sm mb-6">{tier.description}</p>
                                <Button
                                    onClick={() => document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' })}
                                    className={index === 1 
                                        ? "w-full bg-[#00ff41] text-black" 
                                        : "w-full border-[#003b00] text-[#00ff41]"
                                    }
                                    variant={index === 1 ? "default" : "outline"}
                                >
                                    SELECT
                                </Button>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Rent a DM Section */}
            <section className="py-20 px-4">
                <div className="container mx-auto max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="terminal-window p-8 text-center"
                    >
                        <div className="w-16 h-16 bg-[#003b00] rounded-full flex items-center justify-center mx-auto mb-6">
                            <Wand2 className="w-8 h-8 text-[#00ff41]" />
                        </div>
                        <h2 className="text-3xl font-bold text-[#00ff41] mb-4">
                            NEED_A_DUNGEON_MASTER?
                        </h2>
                        <p className="text-xl text-[#008f11] mb-6 max-w-2xl mx-auto">
                            Rent one of our experienced professionals to guide your adventure
                        </p>
                        <p className="text-[#008f11] mb-8 max-w-xl mx-auto">
                            Our skilled Dungeon Masters bring years of storytelling expertise, 
                            custom encounters, and immersive roleplay to elevate your session. 
                            Whether you&apos;re new to tabletop gaming or a seasoned adventurer, 
                            our DMs will craft an unforgettable experience tailored to your group.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                            <div className="bg-[#050505] border border-[#003b00] p-4">
                                <Shield className="w-6 h-6 text-[#00ff41] mx-auto mb-2" />
                                <h4 className="text-[#00ff41] font-bold mb-1">Experienced</h4>
                                <p className="text-[#008f11] text-xs">5+ years average DMing experience</p>
                            </div>
                            <div className="bg-[#050505] border border-[#003b00] p-4">
                                <Sparkles className="w-6 h-6 text-[#00ff41] mx-auto mb-2" />
                                <h4 className="text-[#00ff41] font-bold mb-1">Flexible</h4>
                                <p className="text-[#008f11] text-xs">D&D, Pathfinder, Call of Cthulhu & more</p>
                            </div>
                            <div className="bg-[#050505] border border-[#003b00] p-4">
                                <Users className="w-6 h-6 text-[#00ff41] mx-auto mb-2" />
                                <h4 className="text-[#00ff41] font-bold mb-1">Inclusive</h4>
                                <p className="text-[#008f11] text-xs">Beginner-friendly to hardcore players</p>
                            </div>
                        </div>
                        <div className="bg-[#001a00] border border-[#003b00] p-6 inline-block">
                            <p className="text-[#008f11] text-sm mb-2">DM Rental Rate</p>
                            <p className="text-4xl font-bold text-[#00ff41]">$60<span className="text-lg text-[#008f11]">/hour</span></p>
                            <p className="text-[#008f11] text-xs mt-2">Add to any room booking</p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Booking Section */}
            <section id="booking-section" className="py-20 px-4 bg-[#0a0a0a]">
                <div className="container mx-auto max-w-6xl">
                    <h2 className="text-3xl font-bold text-[#00ff41] text-center mb-4">
                        BOOK_YOUR_SESSION
                    </h2>
                    <p className="text-[#008f11] text-center mb-12">
                        Select a date to see available time slots
                    </p>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Calendar */}
                        <div className="terminal-window p-6">
                            <div className="flex items-center justify-between mb-6">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                    className="text-[#00ff41]"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </Button>
                                <h3 className="text-[#00ff41] font-bold text-lg">
                                    {format(currentMonth, 'MMMM yyyy')}
                                </h3>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                    className="text-[#00ff41]"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </Button>
                            </div>

                            {/* Day headers */}
                            <div className="grid grid-cols-7 gap-1 mb-2">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                    <div key={day} className="text-center text-[#008f11] text-xs py-2">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            {/* Calendar grid */}
                            <div className="grid grid-cols-7 gap-1">
                                {/* Empty cells for start of month */}
                                {[...Array(startOfMonth(currentMonth).getDay())].map((_, i) => (
                                    <div key={`empty-${i}`} className="aspect-square" />
                                ))}
                                
                                {calendarDays.map((date) => {
                                    const status = getDayStatus(date);
                                    const isPast = isBefore(date, new Date()) && !isToday(date);
                                    const isSelected = selectedDate && format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                                    
                                    return (
                                        <button
                                            key={date.toISOString()}
                                            onClick={() => !isPast && status === 'available' && setSelectedDate(date)}
                                            disabled={isPast || status !== 'available'}
                                            className={`
                                                aspect-square flex items-center justify-center text-sm font-mono
                                                transition-colors border
                                                ${isSelected ? 'bg-[#00ff41] text-black border-[#00ff41]' : ''}
                                                ${isToday(date) && !isSelected ? 'border-[#00ff41]' : 'border-transparent'}
                                                ${status === 'available' && !isPast ? 'text-[#00ff41] hover:bg-[#003b00] cursor-pointer' : ''}
                                                ${status === 'booked' ? 'text-[#ff6b6b] cursor-not-allowed' : ''}
                                                ${status === 'blocked' || status === 'unavailable' || isPast ? 'text-[#333] cursor-not-allowed' : ''}
                                            `}
                                        >
                                            {format(date, 'd')}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Legend */}
                            <div className="flex justify-center gap-6 mt-6 text-xs">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-[#00ff41]" />
                                    <span className="text-[#008f11]">Available</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-[#ff6b6b]" />
                                    <span className="text-[#008f11]">Booked</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-[#333]" />
                                    <span className="text-[#008f11]">Unavailable</span>
                                </div>
                            </div>
                        </div>

                        {/* Time Slots */}
                        <div className="terminal-window p-6">
                            <h3 className="text-[#00ff41] font-bold mb-4">
                                {selectedDate 
                                    ? `Available Slots - ${format(selectedDate, 'MMMM d, yyyy')}`
                                    : 'Select a Date'
                                }
                            </h3>

                            {!selectedDate ? (
                                <div className="text-center py-10">
                                    <CalendarIcon className="w-12 h-12 text-[#003b00] mx-auto mb-4" />
                                    <p className="text-[#008f11]">
                                        Choose a date from the calendar to see available time slots
                                    </p>
                                </div>
                            ) : loadingSlots ? (
                                <div className="flex justify-center py-10">
                                    <div className="matrix-loader" />
                                </div>
                            ) : daySlots.length === 0 ? (
                                <div className="text-center py-10">
                                    <X className="w-12 h-12 text-[#003b00] mx-auto mb-4" />
                                    <p className="text-[#008f11]">
                                        No available slots for this date
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {daySlots.map((slot) => (
                                        <button
                                            key={slot.slot_id}
                                            onClick={() => {
                                                if (!slot.is_booked && !slot.is_blocked) {
                                                    setSelectedSlot(slot);
                                                    setShowBookingModal(true);
                                                }
                                            }}
                                            disabled={slot.is_booked || slot.is_blocked}
                                            className={`
                                                w-full p-4 border text-left transition-colors
                                                ${slot.is_booked || slot.is_blocked
                                                    ? 'border-[#333] text-[#333] cursor-not-allowed'
                                                    : 'border-[#003b00] hover:border-[#00ff41] cursor-pointer'
                                                }
                                            `}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Clock className={`w-5 h-5 ${slot.is_booked || slot.is_blocked ? 'text-[#333]' : 'text-[#00ff41]'}`} />
                                                    <span className={slot.is_booked || slot.is_blocked ? 'text-[#333]' : 'text-[#00ff41]'}>
                                                        {slot.start_time} - {slot.end_time}
                                                    </span>
                                                </div>
                                                {slot.is_booked ? (
                                                    <span className="text-xs bg-[#ff6b6b]/20 text-[#ff6b6b] px-2 py-1">
                                                        BOOKED
                                                    </span>
                                                ) : slot.is_blocked ? (
                                                    <span className="text-xs bg-[#333] text-[#666] px-2 py-1">
                                                        BLOCKED
                                                    </span>
                                                ) : (
                                                    <span className="text-xs bg-[#003b00] text-[#00ff41] px-2 py-1">
                                                        AVAILABLE
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Rules Section */}
            <section className="py-20 px-4">
                <div className="container mx-auto max-w-2xl">
                    <h2 className="text-3xl font-bold text-[#00ff41] text-center mb-12">
                        HOUSE_RULES
                    </h2>
                    <div className="terminal-window p-6">
                        <ul className="space-y-4">
                            {info?.rules?.map((rule, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <Shield className="w-5 h-5 text-[#00ff41] flex-shrink-0 mt-0.5" />
                                    <span className="text-[#008f11]">{rule}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Capacity Info */}
            <section className="py-10 px-4 border-t border-[#003b00]">
                <div className="container mx-auto max-w-4xl">
                    <div className="flex justify-center gap-8 flex-wrap">
                        <div className="text-center">
                            <Users className="w-8 h-8 text-[#00ff41] mx-auto mb-2" />
                            <p className="text-[#008f11] text-sm">Capacity</p>
                            <p className="text-[#00ff41] font-bold">
                                {info?.capacity?.min}-{info?.capacity?.max} guests
                            </p>
                        </div>
                        <div className="text-center">
                            <Sparkles className="w-8 h-8 text-[#00ff41] mx-auto mb-2" />
                            <p className="text-[#008f11] text-sm">Optimal Size</p>
                            <p className="text-[#00ff41] font-bold">
                                {info?.capacity?.optimal} players
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Booking Modal */}
            <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl">BOOK_SESSION</DialogTitle>
                        <DialogDescription className="text-[#008f11]">
                            {selectedSlot && `${format(selectedDate, 'MMMM d, yyyy')} • ${selectedSlot.start_time} - ${selectedSlot.end_time}`}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Your Name *</label>
                            <Input
                                data-testid="booking-name"
                                value={bookingForm.guest_name}
                                onChange={(e) => setBookingForm({...bookingForm, guest_name: e.target.value})}
                                placeholder="Enter your name"
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Email *</label>
                            <Input
                                data-testid="booking-email"
                                type="email"
                                value={bookingForm.guest_email}
                                onChange={(e) => setBookingForm({...bookingForm, guest_email: e.target.value})}
                                placeholder="your@email.com"
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Phone (optional)</label>
                            <Input
                                value={bookingForm.guest_phone}
                                onChange={(e) => setBookingForm({...bookingForm, guest_phone: e.target.value})}
                                placeholder="(555) 123-4567"
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Party Size</label>
                                <Select 
                                    value={String(bookingForm.party_size)}
                                    onValueChange={(v) => setBookingForm({...bookingForm, party_size: parseInt(v)})}
                                >
                                    <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                        {[2, 3, 4, 5, 6, 7, 8].map(n => (
                                            <SelectItem key={n} value={String(n)} className="text-[#00ff41]">
                                                {n} guests
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Game Type</label>
                                <Select 
                                    value={bookingForm.game_type}
                                    onValueChange={(v) => setBookingForm({...bookingForm, game_type: v})}
                                >
                                    <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                        <SelectItem value="general" className="text-[#00ff41]">General Gaming</SelectItem>
                                        <SelectItem value="dnd" className="text-[#00ff41]">D&D / TTRPG</SelectItem>
                                        <SelectItem value="mtg" className="text-[#00ff41]">Magic: The Gathering</SelectItem>
                                        <SelectItem value="board_game" className="text-[#00ff41]">Board Games</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Special Requests</label>
                            <textarea
                                value={bookingForm.special_requests}
                                onChange={(e) => setBookingForm({...bookingForm, special_requests: e.target.value})}
                                placeholder="Any special lighting preferences, game setup needs, etc."
                                className="w-full h-20 bg-[#050505] border border-[#003b00] text-[#00ff41] p-2 resize-none"
                            />
                        </div>

                        <Button
                            data-testid="confirm-booking-btn"
                            onClick={handleBooking}
                            className="w-full bg-[#00ff41] text-black"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            CONFIRM_BOOKING
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Admin Panel Modal */}
            <Dialog open={showAdminPanel} onOpenChange={setShowAdminPanel}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl">ADMIN_PANEL</DialogTitle>
                        <DialogDescription className="text-[#008f11]">
                            Manage availability and bookings
                        </DialogDescription>
                    </DialogHeader>

                    <AdminPanel />
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Admin Panel Component
const AdminPanel = () => {
    const [activeTab, setActiveTab] = useState('slots'); // 'slots' | 'block' | 'bookings'
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [blockedDates, setBlockedDates] = useState([]);
    
    // Generate slots form
    const [generateForm, setGenerateForm] = useState({
        startDate: format(new Date(), 'yyyy-MM-dd'),
        endDate: format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
        slots: [
            { start: '10:00', end: '14:00' },
            { start: '15:00', end: '19:00' },
            { start: '20:00', end: '00:00' },
        ]
    });
    
    // Custom slot form
    const [customSlot, setCustomSlot] = useState({
        date: format(new Date(), 'yyyy-MM-dd'),
        start: '10:00',
        end: '14:00'
    });
    
    // Block dates form
    const [blockForm, setBlockForm] = useState({
        startDate: format(new Date(), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
    });

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const response = await fourthDimensionAPI.getBookings();
            setBookings(response.data);
        } catch (error) {
            console.error('Error fetching bookings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (bookingId, status) => {
        try {
            await fourthDimensionAPI.updateBookingStatus(bookingId, status);
            toast.success(`Booking ${status}`);
            fetchBookings();
        } catch (error) {
            toast.error('Failed to update booking');
        }
    };

    const handleGenerateSlots = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/fourth-dimension/admin/generate-slots?` +
                `start_date=${generateForm.startDate}&end_date=${generateForm.endDate}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(generateForm.slots)
                }
            );
            const data = await response.json();
            toast.success(`Generated ${data.created} slots!`);
        } catch (error) {
            toast.error('Failed to generate slots');
        }
    };
    
    const handleAddTimeSlot = () => {
        setGenerateForm({
            ...generateForm,
            slots: [...generateForm.slots, { start: '12:00', end: '16:00' }]
        });
    };
    
    const handleRemoveTimeSlot = (index) => {
        const newSlots = generateForm.slots.filter((_, i) => i !== index);
        setGenerateForm({ ...generateForm, slots: newSlots });
    };
    
    const handleUpdateTimeSlot = (index, field, value) => {
        const newSlots = [...generateForm.slots];
        newSlots[index][field] = value;
        setGenerateForm({ ...generateForm, slots: newSlots });
    };
    
    const handleCreateCustomSlot = async () => {
        try {
            await fourthDimensionAPI.createSlots([{
                date: customSlot.date,
                start_time: customSlot.start,
                end_time: customSlot.end,
                is_blocked: false
            }]);
            toast.success('Custom slot created!');
            setCustomSlot({
                date: format(new Date(), 'yyyy-MM-dd'),
                start: '10:00',
                end: '14:00'
            });
        } catch (error) {
            toast.error('Failed to create slot');
        }
    };
    
    const handleBlockDates = async () => {
        try {
            // Generate array of dates between start and end
            const start = new Date(blockForm.startDate);
            const end = new Date(blockForm.endDate);
            const dates = [];
            
            let current = start;
            while (current <= end) {
                dates.push(format(current, 'yyyy-MM-dd'));
                current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
            }
            
            await fourthDimensionAPI.blockDates(dates, true);
            toast.success(`Blocked ${dates.length} date(s)`);
        } catch (error) {
            toast.error('Failed to block dates');
        }
    };
    
    const handleUnblockDates = async () => {
        try {
            const start = new Date(blockForm.startDate);
            const end = new Date(blockForm.endDate);
            const dates = [];
            
            let current = start;
            while (current <= end) {
                dates.push(format(current, 'yyyy-MM-dd'));
                current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
            }
            
            await fourthDimensionAPI.blockDates(dates, false);
            toast.success(`Unblocked ${dates.length} date(s)`);
        } catch (error) {
            toast.error('Failed to unblock dates');
        }
    };

    return (
        <div className="space-y-4">
            {/* Tab Navigation */}
            <div className="flex border-b border-[#003b00]">
                <button
                    onClick={() => setActiveTab('slots')}
                    className={`px-4 py-2 text-sm font-mono ${
                        activeTab === 'slots' 
                            ? 'text-[#00ff41] border-b-2 border-[#00ff41]' 
                            : 'text-[#008f11] hover:text-[#00ff41]'
                    }`}
                >
                    Time Slots
                </button>
                <button
                    onClick={() => setActiveTab('block')}
                    className={`px-4 py-2 text-sm font-mono ${
                        activeTab === 'block' 
                            ? 'text-[#00ff41] border-b-2 border-[#00ff41]' 
                            : 'text-[#008f11] hover:text-[#00ff41]'
                    }`}
                >
                    Block Dates
                </button>
                <button
                    onClick={() => setActiveTab('bookings')}
                    className={`px-4 py-2 text-sm font-mono ${
                        activeTab === 'bookings' 
                            ? 'text-[#00ff41] border-b-2 border-[#00ff41]' 
                            : 'text-[#008f11] hover:text-[#00ff41]'
                    }`}
                >
                    Bookings ({bookings.filter(b => b.status === 'pending').length})
                </button>
            </div>

            {/* Time Slots Tab */}
            {activeTab === 'slots' && (
                <div className="space-y-6">
                    {/* Bulk Generate Slots */}
                    <div className="border border-[#003b00] p-4">
                        <h3 className="text-[#00ff41] font-bold mb-4">Bulk Generate Slots</h3>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="text-xs text-[#008f11] mb-1 block">Start Date</label>
                                <Input
                                    type="date"
                                    value={generateForm.startDate}
                                    onChange={(e) => setGenerateForm({...generateForm, startDate: e.target.value})}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-[#008f11] mb-1 block">End Date</label>
                                <Input
                                    type="date"
                                    value={generateForm.endDate}
                                    onChange={(e) => setGenerateForm({...generateForm, endDate: e.target.value})}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                        </div>
                        
                        <p className="text-[#008f11] text-xs mb-2">Time slots per day:</p>
                        <div className="space-y-2 mb-4">
                            {generateForm.slots.map((slot, i) => (
                                <div key={`slot-${slot.start}-${slot.end}-${i}`} className="flex items-center gap-2">
                                    <Input
                                        type="time"
                                        value={slot.start}
                                        onChange={(e) => handleUpdateTimeSlot(i, 'start', e.target.value)}
                                        className="bg-[#050505] border-[#003b00] text-[#00ff41] w-28"
                                    />
                                    <span className="text-[#008f11]">to</span>
                                    <Input
                                        type="time"
                                        value={slot.end}
                                        onChange={(e) => handleUpdateTimeSlot(i, 'end', e.target.value)}
                                        className="bg-[#050505] border-[#003b00] text-[#00ff41] w-28"
                                    />
                                    {generateForm.slots.length > 1 && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleRemoveTimeSlot(i)}
                                            className="text-[#ff6b6b] hover:bg-[#ff6b6b]/10 px-2"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                        
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleAddTimeSlot}
                                className="border-[#003b00] text-[#00ff41]"
                            >
                                + Add Slot
                            </Button>
                            <Button
                                onClick={handleGenerateSlots}
                                className="bg-[#00ff41] text-black"
                            >
                                Generate All Slots
                            </Button>
                        </div>
                    </div>
                    
                    {/* Create Single Custom Slot */}
                    <div className="border border-[#003b00] p-4">
                        <h3 className="text-[#00ff41] font-bold mb-4">Create Single Slot</h3>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="text-xs text-[#008f11] mb-1 block">Date</label>
                                <Input
                                    type="date"
                                    value={customSlot.date}
                                    onChange={(e) => setCustomSlot({...customSlot, date: e.target.value})}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-[#008f11] mb-1 block">Start Time</label>
                                <Input
                                    type="time"
                                    value={customSlot.start}
                                    onChange={(e) => setCustomSlot({...customSlot, start: e.target.value})}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-[#008f11] mb-1 block">End Time</label>
                                <Input
                                    type="time"
                                    value={customSlot.end}
                                    onChange={(e) => setCustomSlot({...customSlot, end: e.target.value})}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                        </div>
                        <Button
                            onClick={handleCreateCustomSlot}
                            className="bg-[#00ff41] text-black"
                        >
                            Create Slot
                        </Button>
                    </div>
                </div>
            )}

            {/* Block Dates Tab */}
            {activeTab === 'block' && (
                <div className="border border-[#003b00] p-4">
                    <h3 className="text-[#00ff41] font-bold mb-4">Block / Unblock Dates</h3>
                    <p className="text-[#008f11] text-xs mb-4">
                        Block dates to prevent bookings. Useful for holidays, maintenance, or private events.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="text-xs text-[#008f11] mb-1 block">Start Date</label>
                            <Input
                                type="date"
                                value={blockForm.startDate}
                                onChange={(e) => setBlockForm({...blockForm, startDate: e.target.value})}
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-[#008f11] mb-1 block">End Date</label>
                            <Input
                                type="date"
                                value={blockForm.endDate}
                                onChange={(e) => setBlockForm({...blockForm, endDate: e.target.value})}
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>
                    </div>
                    
                    <p className="text-[#008f11] text-xs mb-4">
                        Tip: Set both dates to the same day to block/unblock a single date.
                    </p>
                    
                    <div className="flex gap-2">
                        <Button
                            onClick={handleBlockDates}
                            className="bg-[#ff6b6b] text-black hover:bg-[#ff6b6b]/80"
                        >
                            Block Dates
                        </Button>
                        <Button
                            onClick={handleUnblockDates}
                            variant="outline"
                            className="border-[#00ff41] text-[#00ff41]"
                        >
                            Unblock Dates
                        </Button>
                    </div>
                </div>
            )}

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
                <div className="border border-[#003b00] p-4">
                    <h3 className="text-[#00ff41] font-bold mb-4">All Bookings</h3>
                    {loading ? (
                        <div className="matrix-loader mx-auto" />
                    ) : bookings.length === 0 ? (
                        <p className="text-[#008f11] text-sm">No bookings yet</p>
                    ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {bookings.map((booking) => (
                                <div key={booking.booking_id} className="bg-[#050505] border border-[#003b00] p-3">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <p className="text-[#00ff41] font-bold">{booking.guest_name}</p>
                                            <p className="text-[#008f11] text-xs">{booking.guest_email}</p>
                                            {booking.guest_phone && (
                                                <p className="text-[#008f11] text-xs">{booking.guest_phone}</p>
                                            )}
                                        </div>
                                        <span className={`text-xs px-2 py-1 ${
                                            booking.status === 'confirmed' ? 'bg-[#003b00] text-[#00ff41]' :
                                            booking.status === 'cancelled' ? 'bg-[#330000] text-[#ff6b6b]' :
                                            'bg-[#333] text-[#00ff41]'
                                        }`}>
                                            {booking.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <p className="text-[#008f11] text-sm">
                                        {booking.date} • {booking.start_time}-{booking.end_time}
                                    </p>
                                    <p className="text-[#008f11] text-sm">
                                        {booking.party_size} guests • {booking.game_type}
                                    </p>
                                    {booking.special_requests && (
                                        <p className="text-[#008f11] text-xs mt-1 italic">
                                            &ldquo;{booking.special_requests}&rdquo;
                                        </p>
                                    )}
                                    {booking.status === 'pending' && (
                                        <div className="flex gap-2 mt-3">
                                            <Button
                                                size="sm"
                                                onClick={() => handleStatusUpdate(booking.booking_id, 'confirmed')}
                                                className="bg-[#00ff41] text-black"
                                            >
                                                <Check className="w-3 h-3 mr-1" />
                                                Confirm
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleStatusUpdate(booking.booking_id, 'cancelled')}
                                                className="border-[#ff6b6b] text-[#ff6b6b]"
                                            >
                                                <X className="w-3 h-3 mr-1" />
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FourthDimension;
